import { type FormEvent, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Alert, AuthLayout, Button, Field, TextLink } from '../components/ui';
import { authClient, authErrorMessage } from '../lib/auth-client';

/**
 * Landing page of the reset email; the API appends `?token=` (or `?error=`). `?setup=1` marks
 * the first password of an approved developer's new account (the welcome email's link).
 */
export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const setup = params.get('setup') === '1';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (!token || params.has('error')) {
    return (
      <AuthLayout title="This link didn’t work">
        <p className="text-ink">
          {setup ? 'Setup links work once, for 48 hours.' : 'Reset links work once, for 1 hour.'}{' '}
          <TextLink to="/forgot-password">Ask for a new one</TextLink>
          {setup && ' with the email address you applied with'}.
        </p>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout title={setup ? 'Your account is ready' : 'Password changed'}>
        {setup ? (
          <p className="text-ink">
            <TextLink to="/sign-in">Sign in</TextLink> with your new password, then turn on
            two-factor authentication in your account settings. Developers need it.
          </p>
        ) : (
          <p className="text-ink">
            You’ve been signed out everywhere else. <TextLink to="/sign-in">Sign in</TextLink> with
            your new password.
          </p>
        )}
      </AuthLayout>
    );
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (password !== confirm) return setError('The two passwords don’t match.');
    setBusy(true);
    setError(null);
    const { error: failed } = await authClient.resetPassword({
      newPassword: password,
      token: token ?? '',
    });
    setBusy(false);
    if (failed) return setError(authErrorMessage(failed));
    setDone(true);
  }

  return (
    <AuthLayout
      title={setup ? 'Set up your developer account' : 'Choose a new password'}
      subtitle={setup ? 'Choose the password you’ll sign in with.' : undefined}
    >
      <form onSubmit={onSubmit} className="space-y-4">
        {error && <Alert>{error}</Alert>}
        <Field
          label="New password"
          name="password"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          maxLength={128}
          hint="At least 8 characters."
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        <Field
          label="Repeat new password"
          name="confirm"
          type="password"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
        />
        <Button busy={busy}>{setup ? 'Set password' : 'Change password'}</Button>
      </form>
    </AuthLayout>
  );
}
